import React, {useEffect, useState} from 'react';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {Label} from "@/components/ui/label";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {Switch} from "@/components/ui/switch";
import {Button} from "@/components/ui/button";
// Removed tabs import - now using single page layout
import {Separator} from "@/components/ui/separator";
import {Bot, FileText, MessageSquare, Plus, Settings, X} from "lucide-react";
import {useToast} from "@/components/ui/use-toast";
import {useBuilder} from "@/contexts/BuilderContext";
import {AIChatModeConfig, getDefaultAIChatConfigActions} from "@/hooks/useAIChatModeConfig";
import {DEFAULT_UNIFIED_SETTINGS} from "@/types/builder";

// Drag and drop imports
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from "@/components/ui/dropdown-menu";

// Custom sortable components
import {SortableActionItem} from './SortableActionItem';
import {v4 as uuidv4} from "uuid";

// No props needed - using unified context instead
interface AIChatModeSettingsProps {}

export const AIChatModeSettings: React.FC<AIChatModeSettingsProps> = () => {
  const { toast } = useToast();
  
  // Get data from unified context
  const { getChatbot, getSettings, updateSettings, updateBasicInfo, state, debouncedSave, getFieldsDictionary } = useBuilder();
  const chatbot = getChatbot();
  const settings = getSettings();
  const fieldsDictionary = getFieldsDictionary();

  // Get AI config from unified settings with proper defaults
  const config = settings?.ai_chat_config || DEFAULT_UNIFIED_SETTINGS.ai_chat_config;

  const defaultAIChatConfigActions = getDefaultAIChatConfigActions();
  
  // Helper function to update config
  const updateConfig = (updates: Partial<AIChatModeConfig>) => {
    console.log('=== UPDATE CONFIG DEBUG ===');
    console.log('updateConfig called with:', updates);
    console.log('Current config before update:', config);
    const newConfig = { ...config, ...updates };
    console.log('New config after merge:', newConfig);
    console.log('Calling updateSettings with ai_chat_config:', newConfig);
    updateSettings({ ai_chat_config: newConfig });
    console.log('=== END UPDATE CONFIG DEBUG ===');
  };
  
  // Handle welcome message from chatbot
  const [welcomeMessage, setWelcomeMessage] = useState(chatbot?.welcome_message || '');


  // Update welcome message when chatbot data changes
  useEffect(() => {
    if (chatbot?.welcome_message !== undefined) {
      setWelcomeMessage(chatbot.welcome_message);
    }
  }, [chatbot?.welcome_message]);

  const handleWelcomeMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setWelcomeMessage(newValue);
    // Update context (will be saved via main Save Changes button)
    updateBasicInfo({ welcome_message: newValue });
  };

  const showWelcomeSuccess = false; // This can be managed by parent if needed

  // Helper function to add/remove array items
  const addArrayItem = (field: keyof AIChatModeConfig, item: string) => {
    const currentArray = config[field] as string[];
    updateConfig({ [field]: [...currentArray, item] });
  };

  const removeArrayItem = (field: keyof AIChatModeConfig, index: number) => {
    const currentArray = config[field] as any[];
    updateConfig({ [field]: currentArray.filter((_, i) => i !== index) });
  };



  // // Helper function to update field safely
  // const updateFieldItem = (field: 'requiredFields' | 'optionalFields', index: number, updates: Partial<{ name: string; description: string }>) => {
  //   const currentArray = config[field] as Array<{ id: string; name: string; description: string }>;
  //   const newArray = [...currentArray];
  //   newArray[index] = { ...newArray[index], ...updates };
  //   console.log(`✏️ Updating field in ${field} at index ${index}:`, updates);
  //   updateConfig({ [field]: newArray });
  // };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle goal reordering (priority)
  const handleActionDragEnd = (event: DragEndEvent, goalIndex: number) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const currentGoal = config.offerGoals?.goals?.[goalIndex];
      const actions = currentGoal?.actions || [];
      const oldIndex = actions.findIndex(a => a.id === active.id);
      const newIndex = actions.findIndex(a => a.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedActions = arrayMove(actions, oldIndex, newIndex);
        
        // Update priority property
        const updatedActions = reorderedActions.map((action, index) => ({
          ...action,
          priority: index
        }));

        console.log('🔄 Reordered actions (priority):', updatedActions.map(a => a.name));
        
        const newGoals = [...(config.offerGoals?.goals || [])];
        if (newGoals[goalIndex]) {
          newGoals[goalIndex] = {
            ...newGoals[goalIndex],
            actions: updatedActions
          };

          updateConfig({
          offerGoals: {
              ...config.offerGoals,
              goals: newGoals
            }
          });
        }
      }
    }
  };

  // Normalize priority sequentially based on current order
  const normalizeActionPriorities = (actions: NonNullable<AIChatModeConfig['offerGoals']>['goals'][number]['actions']) =>
    actions.map((action, idx) => ({ ...action, priority: idx }));

  // Offer update handlers
  const handleUpdateAction = (goalIndex: number, actionIndex: number, updates: Partial<NonNullable<AIChatModeConfig['offerGoals']>['goals'][number]['actions'][number]>) => {
    if (!config.offerGoals?.goals) return;

    const newGoals = [...config.offerGoals.goals];
    if (!newGoals[goalIndex]) return;
    const targetGoal = { ...newGoals[goalIndex] };
    
    const newActions = [...(targetGoal.actions || [])];
    
    if (newActions[actionIndex]) {
      newActions[actionIndex] = { ...newActions[actionIndex], ...updates };
      targetGoal.actions = newActions;
      newGoals[goalIndex] = targetGoal;

      updateConfig({
        offerGoals: {
          ...config.offerGoals,
          goals: newGoals
        }
      });
    }
  };

  const handleDeleteAction = (goalIndex: number, actionIndex: number) => {
    if (!config.offerGoals?.goals) return;

    const newGoals = [...config.offerGoals.goals];
    if (!newGoals[goalIndex]) return;
    const targetGoal = { ...newGoals[goalIndex] };

    targetGoal.actions = normalizeActionPriorities((targetGoal.actions || []).filter((_, i) => i !== actionIndex));
    newGoals[goalIndex] = targetGoal;

    updateConfig({
      offerGoals: {
        ...config.offerGoals,
        goals: newGoals
      }
    });
  };

  const handleAddAction = (goalIndex: number, actionId: string) => {
    if (!config.offerGoals?.goals) return;
    const template = defaultAIChatConfigActions.find(a => a.id === actionId);
    if (!template) return;

    const newGoals = [...config.offerGoals.goals];
    const targetGoal = { ...newGoals[goalIndex] };
    const actions = normalizeActionPriorities([...(targetGoal.actions || [])]);
    const cloned = {
      ...template,
      id: uuidv4(),
      priority: actions.length
    };
    actions.push(cloned);
    targetGoal.actions = actions;
    newGoals[goalIndex] = targetGoal;

    updateConfig({
      offerGoals: {
        ...config.offerGoals,
        goals: newGoals
      }
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Bot className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">AI-Only Chat Configuration</h2>
            <p className="text-sm text-muted-foreground">
              Configure your AI assistant's personality, behavior, and conversation guidelines
            </p>
          </div>
        </div>
      </div>



      {/* 2-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Left Column - Core Configuration */}
        <div className="space-y-6">
          {/* AI Personality Section */}
          <Card className="bg-slate-100">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Bot className="h-5 w-5" />
            <span>AI Personality</span>
          </CardTitle>
          <CardDescription>
            Define your AI assistant's identity and conversation style
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ai-name">Assistant Name</Label>
                  <Input
                    id="ai-name"
                    value={config.aiPersonality.name}
                    onChange={(e) => updateConfig({
                      aiPersonality: { ...config.aiPersonality, name: e.target.value }
                    })}
                    placeholder="e.g., Alex, Customer Support Bot"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ai-role">Role/Title</Label>
                  <Input
                    id="ai-role"
                    value={config.aiPersonality.role}
                    onChange={(e) => updateConfig({
                      aiPersonality: { ...config.aiPersonality, role: e.target.value }
                    })}
                    placeholder="e.g., Customer Service Representative"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="ai-description">Personality Description</Label>
                <Textarea
                  id="ai-description"
                  value={config.aiPersonality.description}
                  onChange={(e) => updateConfig({
                    aiPersonality: { ...config.aiPersonality, description: e.target.value }
                  })}
                  placeholder="Describe your AI's personality, expertise, and approach to conversations..."
                  rows={3}
                />
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Conversation Style</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Tone</Label>
                    <select 
                      className="w-full p-2 border rounded-md"
                      value={config.conversationStyle.tone}
                      onChange={(e) => updateConfig({
                        conversationStyle: { 
                          ...config.conversationStyle, 
                          tone: e.target.value as any 
                        }
                      })}
                    >
                      <option value="professional">Professional</option>
                      <option value="friendly">Friendly</option>
                      <option value="casual">Casual</option>
                      <option value="formal">Formal</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Response Length</Label>
                    <select 
                      className="w-full p-2 border rounded-md"
                      value={config.conversationStyle.responseLength}
                      onChange={(e) => updateConfig({
                        conversationStyle: { 
                          ...config.conversationStyle, 
                          responseLength: e.target.value as any 
                        }
                      })}
                    >
                      <option value="concise">Concise</option>
                      <option value="detailed">Detailed</option>
                      <option value="adaptive">Adaptive</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Proactiveness</Label>
                    <select 
                      className="w-full p-2 border rounded-md"
                      value={config.conversationStyle.proactiveness}
                      onChange={(e) => updateConfig({
                        conversationStyle: { 
                          ...config.conversationStyle, 
                          proactiveness: e.target.value as any 
                        }
                      })}
                    >
                      <option value="reactive">Reactive</option>
                      <option value="balanced">Balanced</option>
                      <option value="proactive">Proactive</option>
                    </select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

      {/* System Instructions Section */}
      <Card className="bg-slate-100">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>System Instructions</span>
          </CardTitle>
          <CardDescription>
            Define how your AI should behave, respond to users, and greet them initially
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
                       {/* Welcome Message Section */}
               <div className="space-y-2">
                 <Label htmlFor="welcome-message">Welcome Message</Label>
                 {showWelcomeSuccess && (
                   <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded relative mb-2">
                     <span className="block sm:inline">Welcome message saved successfully!</span>
                   </div>
                 )}
                 <Textarea
                   id="welcome-message"
                   value={welcomeMessage}
                   onChange={handleWelcomeMessageChange}
                   placeholder="Hello! I'm your AI assistant. How can I help you today?"
                   rows={3}
                 />
                 <p className="text-xs text-muted-foreground">
                   The initial message users will see when they start an AI conversation.
                 </p>
               </div>
               </CardContent>

               <Separator />
            <CardContent className="space-y-4">
               <div className="space-y-2">
                 <Label htmlFor="system-instructions">Core Instructions</Label>
                 <Textarea
                   id="system-instructions"
                   value={config.systemInstructions}
                   onChange={(e) => updateConfig({ systemInstructions: e.target.value })}
                   placeholder="Provide detailed instructions on how the AI should behave, what it should focus on, and how it should help users..."
                   rows={6}
                 />
                 <p className="text-xs text-muted-foreground">
                   These instructions will be used as the system prompt for your AI assistant.
                 </p>
               </div>

               <Separator />



              <div className="space-y-3">
                <Label>Conversation Guidelines</Label>
                <div className="space-y-2">
                  {config.conversationGuidelines.map((guideline, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Input
                        value={guideline}
                        onChange={(e) => {
                          const newGuidelines = [...config.conversationGuidelines];
                          newGuidelines[index] = e.target.value;
                          updateConfig({ conversationGuidelines: newGuidelines });
                        }}
                        placeholder="Enter a conversation guideline..."
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeArrayItem('conversationGuidelines', index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addArrayItem('conversationGuidelines', '')}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Guideline
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Prohibited Topics</Label>
                <div className="space-y-2">
                  {config.prohibitedTopics.map((topic, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Input
                        value={topic}
                        onChange={(e) => {
                          const newTopics = [...config.prohibitedTopics];
                          newTopics[index] = e.target.value;
                          updateConfig({ prohibitedTopics: newTopics });
                        }}
                        placeholder="Enter a prohibited topic..."
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeArrayItem('prohibitedTopics', index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addArrayItem('prohibitedTopics', '')}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Prohibited Topic
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Advanced Settings Section */}
          <Card className="bg-slate-100">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="h-5 w-5" />
                <span>Advanced Settings</span>
              </CardTitle>
              <CardDescription>
                Configure advanced conversation management and behavior
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fallback-message">Fallback Message</Label>
                <Textarea
                    id="fallback-message"
                    value={config.fallbackMessage}
                    onChange={(e) => updateConfig({ fallbackMessage: e.target.value })}
                    placeholder="Message to show when the AI cannot understand or respond appropriately..."
                    rows={2}
                />
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Context Memory</Label>
                    <p className="text-sm text-muted-foreground">
                      Remember conversation history
                    </p>
                  </div>
                  <Switch
                      checked={config.contextMemory}
                      onCheckedChange={(checked) => updateConfig({ contextMemory: checked })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max-length">Max Conversation Length</Label>
                  <Input
                      id="max-length"
                      type="number"
                      value={config.maxConversationLength}
                      onChange={(e) => updateConfig({ maxConversationLength: parseInt(e.target.value) || 50 })}
                      placeholder="50"
                      min="10"
                      max="200"
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximum number of messages in conversation
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <Label>End Conversation Triggers</Label>
                <p className="text-sm text-muted-foreground">
                  Keywords or phrases that should end the conversation
                </p>
                <div className="space-y-2">
                  {config.endConversationTriggers.map((trigger, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Input
                            value={trigger}
                            onChange={(e) => {
                              const newTriggers = [...config.endConversationTriggers];
                              newTriggers[index] = e.target.value;
                              updateConfig({ endConversationTriggers: newTriggers });
                            }}
                            placeholder="e.g., goodbye, end chat, no thanks"
                        />
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeArrayItem('endConversationTriggers', index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                  ))}
                  <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('endConversationTriggers', '')}
                      className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add End Trigger
                  </Button>
                </div>
              </div>

              {/* Escalation Triggers - Hidden for now */}
              {false && (
                  <div className="space-y-3">
                    <Label>Escalation Triggers</Label>
                    <p className="text-sm text-muted-foreground">
                      Keywords that should escalate to human support
                    </p>
                    <div className="space-y-2">
                      {config.escalationTriggers.map((trigger, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <Input
                                value={trigger}
                                onChange={(e) => {
                                  const newTriggers = [...config.escalationTriggers];
                                  newTriggers[index] = e.target.value;
                                  updateConfig({ escalationTriggers: newTriggers });
                                }}
                                placeholder="e.g., speak to human, manager, complaint"
                            />
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => removeArrayItem('escalationTriggers', index)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                      ))}
                      <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addArrayItem('escalationTriggers', '')}
                          className="w-full"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Escalation Trigger
                      </Button>
                    </div>
                  </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Data & Advanced Settings */}
        <div className="space-y-6">
          {config.offerGoals?.goals?.map((goal, goalIndex) => (
          <Card key={goal.id} className="bg-slate-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                {goal.name}
              </CardTitle>
              <CardDescription>
                Configure conversation offer with action-based flows
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                  <>

                    <div className="space-y-3">
                      <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => handleActionDragEnd(event, goalIndex)}
                      >
                        <SortableContext
                            items={goal.actions?.map(a => a.id) || []}
                            strategy={verticalListSortingStrategy}
                        >
                          {goal.actions?.map((action, actionIndex) => (
                              <SortableActionItem
                                  key={action.id}
                                  action={action}
                                  actionIndex={actionIndex}
                                  allActions={goal.actions || []}
                                  onUpdateAction={(updates) => handleUpdateAction(goalIndex, actionIndex, updates)}
                                  onDeleteAction={() => handleDeleteAction(goalIndex, actionIndex)}
                                  fieldsDictionary={fieldsDictionary}
                              />
                          ))}
                        </SortableContext>
                      </DndContext>
                      <div className="pt-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="w-full">
                              <Plus className="h-4 w-4 mr-2" />
                              New Goal
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="w-full">
                            {[...defaultAIChatConfigActions]
                              .sort((a, b) => {
                                const aUsed = goal.actions?.some(act => act.type === a.type) ?? false;
                                const bUsed = goal.actions?.some(act => act.type === b.type) ?? false;
                                return Number(aUsed) - Number(bUsed); // enabled (false) first
                              })
                              .map((actionTemplate) => (
                                <DropdownMenuItem
                                  key={actionTemplate.id}
                                  disabled={goal.actions?.some(a => a.type === actionTemplate.type)}
                                  onSelect={() => handleAddAction(goalIndex, actionTemplate.id)}
                                >
                                  {actionTemplate.name || actionTemplate.description || actionTemplate.type}
                                </DropdownMenuItem>
                              ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </>
            </CardContent>
          </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
